import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useToastPopup } from '@collable/ui'

export interface CollableMutationOptions<TMutationFn extends (...args: any) => any> {
  onSuccess?: (data: Awaited<ReturnType<TMutationFn>>) => void
  onError?: (error: any) => void
  onSettled?: (data: Awaited<ReturnType<TMutationFn>>, error: any) => void
  onMutate?: (data: Parameters<TMutationFn>[0]) => void
}

interface UseCollableMutationProps<TMutationFn extends (...args: any) => any>
  extends CollableMutationOptions<TMutationFn> {
  mutationFn: TMutationFn
}

interface CollableError {
  timestamp: string
  status: number
  code?: string
  error?: string
  message?: string
}

export const useCollableMutation = <
  TMutationFn extends (...args: any) => any,
  TError extends CollableError = CollableError & unknown,
>({
  mutationFn,
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: UseCollableMutationProps<TMutationFn>) => {
  const { addToastPopup } = useToastPopup()
  return useMutation<
    Awaited<ReturnType<TMutationFn>>,
    AxiosError<TError>,
    Parameters<TMutationFn>[0]
  >({
    mutationFn,
    onSuccess(data) {
      onSuccess?.(data)
    },
    onError(error) {
      const errorMessage = error.response?.data.message
      if (errorMessage) {
        addToastPopup({
          type: 'normal',
          message: errorMessage,
          status: 'danger',
        })
      }
      onError?.(error)
    },
    onSettled(data, error) {
      onSettled?.(data!, error)
    },
    onMutate(data) {
      onMutate?.(data)
    },
  })
}
